import React, { useState, useEffect } from 'react';
import { Calendar, TrendingUp, Shield, AlertTriangle, RefreshCw, Cloud, Newspaper } from 'lucide-react';

const NewsHub = () => {
  const [news, setNews] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);

  // Static blog posts from CyberTek
  const blogPosts = [
    {
      title: 'Ransomware in 2026: What Small Businesses Must Do Now',
      date: '2026-06-24',
      excerpt: 'Ransomware groups hit record numbers of organizations this year — and SMBs are prime targets. Here are the five defenses every business needs: MFA, patching, tested backups, endpoint protection, and staff training.',
      category: 'Security',
    },
    {
      title: 'Patch Tuesday Explained: Why June 2026 Was a Wake-Up Call',
      date: '2026-06-17',
      excerpt: "Microsoft's largest-ever security update fixed 200 vulnerabilities including six zero-days. Learn why timely patch management is one of the cheapest and most effective protections for your office network.",
      category: 'Security',
    },
    {
      title: 'AI Agents Are Coming to Your Office: Microsoft 365 Copilot in 2026',
      date: '2026-06-10',
      excerpt: 'With Copilot Cowork now generally available, AI agents can handle multi-step tasks in Teams, Outlook, and SharePoint. We break down what it means for your licensing, governance, and productivity.',
      category: 'Productivity',
    },
    {
      title: 'Is Your VPN a Backdoor? Lessons from the Check Point Exploit',
      date: '2026-06-05',
      excerpt: 'A critical VPN flaw let attackers log in without a password. If your business relies on remote access, here is how to verify your firewall and VPN are configured — and patched — correctly.',
      category: 'Cloud',
    },
  ];

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/news`);
      const data = await response.json();
      setNews(data.articles || []);
      setLastUpdated(data.last_updated);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'vulnerability':
        return <AlertTriangle className="text-orange-500" size={20} />;
      case 'breach':
        return <Shield className="text-red-500" size={20} />;
      case 'threat':
        return <TrendingUp className="text-yellow-500" size={20} />;
      case 'patch':
        return <Shield className="text-green-500" size={20} />;
      default:
        return <Newspaper className="text-blue-500" size={20} />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category?.toLowerCase()) {
      case 'security':
        return 'bg-red-100 text-red-600';
      case 'threat':
        return 'bg-yellow-100 text-yellow-700';
      case 'breach':
        return 'bg-red-100 text-red-700';
      case 'cloud':
        return 'bg-blue-100 text-blue-600';
      case 'productivity':
        return 'bg-green-100 text-green-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">CyberTek News Center</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Stay informed with the latest tech news, cybersecurity alerts, and industry insights
          </p>
        </div>
      </section>

      {/* Content Hub */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Blog Section */}
            <div>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-3xl font-bold text-gray-900">CyberTek Blog</h2>
              </div>
              
              <div className="space-y-6">
                {blogPosts.map((post, index) => (
                  <div
                    key={index}
                    className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-300 cursor-pointer"
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <span className="px-3 py-1 bg-blue-100 text-[#2995FF] rounded-full text-xs font-semibold">
                        {post.category}
                      </span>
                      <div className="flex items-center space-x-1 text-sm text-gray-500">
                        <Calendar size={14} />
                        <span>{formatDate(post.date)}</span>
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 hover:text-[#2995FF] transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-gray-600">{post.excerpt}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Tech & Security News Feed */}
            <div>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-3xl font-bold text-gray-900">Tech & Security News</h2>
                <span className="px-3 py-1 bg-green-100 text-green-600 rounded-full text-xs font-semibold flex items-center space-x-1">
                  <RefreshCw size={12} />
                  <span>Auto-Updated</span>
                </span>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="animate-spin text-[#2995FF]" size={32} />
                </div>
              ) : (
                <div className="space-y-4">
                  {news.length > 0 ? (
                    news.map((article, index) => (
                      <div
                        key={article.id || index}
                        className="bg-white rounded-xl p-5 border-l-4 border-[#2995FF] shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer"
                      >
                        <div className="flex items-start space-x-3">
                          <div className="mt-1">{getTypeIcon(article.type)}</div>
                          <div className="flex-1">
                            <div className="flex items-center flex-wrap gap-2 mb-2">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getCategoryColor(article.category)}`}>
                                {article.category}
                              </span>
                              <span className="text-xs text-gray-500">{article.source}</span>
                              <span className="text-xs text-gray-400">•</span>
                              <span className="text-xs text-gray-500">{formatDate(article.date)}</span>
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2 hover:text-[#2995FF] transition-colors">
                              {article.title}
                            </h3>
                            <p className="text-sm text-gray-600">{article.excerpt}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <Newspaper size={48} className="mx-auto mb-4 opacity-50" />
                      <p>No news articles available at the moment.</p>
                    </div>
                  )}
                </div>
              )}

              {lastUpdated && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg text-center">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Last updated:</span> {formatDate(lastUpdated)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    News is updated regularly with the latest cybersecurity alerts and tech industry updates
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#2995FF] to-[#004AAD]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Stay Protected</h2>
          <p className="text-xl text-blue-100 mb-8">
            Contact us to learn how CyberTek can help protect your business from the latest threats
          </p>
          <a
            href="/contact"
            className="inline-block px-8 py-4 bg-white text-[#2995FF] rounded-lg font-semibold hover:shadow-lg transition-all duration-300"
          >
            Contact Us Today
          </a>
        </div>
      </section>
    </div>
  );
};

export default NewsHub;
